import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Filter, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import api from '../utils/api';

type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'failed';

interface OrderItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  selectedColor?: string;
}

interface CustomerInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone: string;
  address: string;
  city: string;
  postalCode?: string;
  country?: string;
  region?: string;
  municipality?: string;
  altPhone?: string;
}

interface Order {
  _id: string;
  orderNumber?: string;
  items: OrderItem[];
  customerInfo: CustomerInfo;
  totalAmount: number;
  status: OrderStatus;
  paymentMethod?: 'cash_on_delivery' | 'reflect_wallet' | 'bank_transfer' | 'card';
  paymentIntentId?: string;
  trackingNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface PagedOrdersResponse {
  orders: Order[];
  totalPages: number;
  currentPage: number;
  total: number;
}

const STATUS_OPTIONS: OrderStatus[] = ['pending', 'processing', 'shipped', 'delivered', 'cancelled', 'failed'];
const STATUS_LABELS_AR: Record<OrderStatus, string> = {
  pending: 'قيد الانتظار',
  processing: 'قيد المعالجة',
  shipped: 'تم الشحن',
  delivered: 'تم التسليم',
  cancelled: 'ملغي',
  failed: 'فشل'
};

const PAYMENT_LABELS_AR: Record<string, string> = {
  reflect_wallet: 'محفظة ريفلكت',
  cash_on_delivery: 'الدفع عند الاستلام',
  bank_transfer: 'تحويل بنكي',
  card: 'بطاقة',
};

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [emailQuery, setEmailQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [orderPendingDelete, setOrderPendingDelete] = useState<Order | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const fetchOrders = async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const params: Record<string, any> = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (emailQuery.trim()) params.email = emailQuery.trim();

      const { data } = await api.get<PagedOrdersResponse>('/api/orders/admin/all', { params });
      setOrders(data.orders);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Failed to fetch orders', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refresh = () => fetchOrders(true);

  const formatCurrency = (amount: number) => `₪${amount.toFixed(2)}`;

  const handleApplyFilters = () => {
    setPage(1);
    fetchOrders();
  };

  const clearFilters = () => {
    setEmailQuery('');
    setStatusFilter('');
    setPage(1);
    fetchOrders();
  };

  const statusBadgeClass = (status: OrderStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-gray-200 text-gray-700';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    const prev = orders;
    // Optimistic update
    setOrders((curr) => curr.map(o => o._id === orderId ? { ...o, status: newStatus } : o));
    try {
      await api.put(`/api/orders/${orderId}/status`, { status: newStatus });
    } catch (e) {
      console.error('Failed to update status', e);
      // Revert on error
      setOrders(prev);
      alert('فشل تحديث حالة الطلب');
    }
  };

  const requestDeleteOrder = (order: Order) => {
    setOrderPendingDelete(order);
  };

  const cancelDeleteOrder = () => {
    if (deletingOrderId) return;
    setOrderPendingDelete(null);
  };

  const confirmDeleteOrder = async () => {
    if (!orderPendingDelete || deletingOrderId) return;

    try {
      setDeletingOrderId(orderPendingDelete._id);
      await api.delete(`/api/orders/admin/${orderPendingDelete._id}`);
      setOrders((curr) => curr.filter((o) => o._id !== orderPendingDelete._id));
      if (expandedOrderId === orderPendingDelete._id) {
        setExpandedOrderId(null);
      }
      setOrderPendingDelete(null);
    } catch (e) {
      console.error('Failed to delete order', e);
      alert('فشل حذف الطلب');
    } finally {
      setDeletingOrderId(null);
    }
  };

  const orderSummary = useMemo(() => {
    const total = orders.length;
    const byStatus = STATUS_OPTIONS.reduce<Record<string, number>>((acc, s) => {
      acc[s] = orders.filter(o => o.status === s).length;
      return acc;
    }, {} as Record<string, number>);
    return { total, byStatus };
  }, [orders]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600 dark:text-gray-400 transition-colors duration-300">جاري تحميل الطلبات...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white transition-colors duration-300">طلبات العملاء</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 transition-colors duration-300">عرض الطلبات المقدمة من الزوار بكامل التفاصيل</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} disabled={refreshing} className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-dark-textSecondary hover:text-gray-900 dark:hover:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ml-2 ${refreshing ? 'animate-spin' : ''}`} /> تحديث
          </button>
        </div>
      </div>

      <div className="mb-6 bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 sm:p-6 transition-colors duration-300">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">البحث بالبريد الإلكتروني</label>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={emailQuery} onChange={(e) => setEmailQuery(e.target.value)} placeholder="example@email.com" className="w-full pl-3 pr-9 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-textSecondary" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">الحالة</label>
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full pr-9 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text">
                <option value="">الكل</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 md:justify-end">
            <button onClick={handleApplyFilters} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">تطبيق</button>
            <button onClick={clearFilters} className="px-4 py-2 bg-gray-100 dark:bg-dark-hover hover:bg-gray-200 dark:hover:bg-dark-bg text-gray-700 dark:text-dark-text rounded-lg transition-colors">مسح</button>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-dark-surface rounded-lg p-3 border border-gray-200 dark:border-dark-border text-right">
          <div className="text-xs text-gray-500 dark:text-gray-400">إجمالي</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{orderSummary.total}</div>
        </div>
        {STATUS_OPTIONS.map(s => (
          <div key={s} className="bg-white dark:bg-dark-surface rounded-lg p-3 border border-gray-200 dark:border-dark-border text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400">{STATUS_LABELS_AR[s as OrderStatus]}</div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{orderSummary.byStatus[s]}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-dark-hover">
            <tr>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">رقم الطلب</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">العميل</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">البريد</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">الهاتف</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">الإجمالي</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">بوابة الدفع</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">الحالة</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">التاريخ</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
            {orders.map(order => {
              const orderName = [order.customerInfo.firstName, order.customerInfo.lastName].filter(Boolean).join(' ') || 'ضيف';
              const isExpanded = expandedOrderId === order._id;
              const isReflect = order.paymentMethod === 'reflect_wallet';
              return (
                <React.Fragment key={order._id}>
                  <tr className={`${isReflect ? 'bg-pink-50 dark:bg-rose-900/20' : ''} hover:bg-gray-50 dark:hover:bg-dark-hover/70`}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{order.orderNumber || order._id.slice(-8)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{orderName}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{order.customerInfo.email || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{order.customerInfo.phone}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(order.totalAmount)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                      {order.paymentMethod ? (
                        <span className={`px-2 py-1 rounded-full text-xs ${isReflect ? 'bg-pink-200 text-pink-900 dark:bg-rose-800 dark:text-rose-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                          {PAYMENT_LABELS_AR[order.paymentMethod] || '-'}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm"><span className={`px-2 py-1 rounded-full text-xs ${statusBadgeClass(order.status)}`}>{STATUS_LABELS_AR[order.status]}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{new Date(order.createdAt).toLocaleString('en-GB')}</td>
                    <td className="px-4 py-3 text-left">
                      <button onClick={() => setExpandedOrderId(isExpanded ? null : order._id)} className="inline-flex items-center px-2 py-1 text-sm text-primary-600 dark:text-primary-400 hover:underline">
                        {isExpanded ? (<><ChevronUp className="w-4 h-4 ml-1" />إخفاء</>) : (<><ChevronDown className="w-4 h-4 ml-1" />تفاصيل</>)}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={`${isReflect ? 'bg-pink-50/70 dark:bg-rose-900/30' : 'bg-gray-50/60 dark:bg-dark-hover/40'}`}>
                      <td colSpan={9} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">تفاصيل العميل</h3>
                            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                              <div>الاسم: {orderName}</div>
                              <div>الهاتف: {order.customerInfo.phone}{order.customerInfo.altPhone ? ` / ${order.customerInfo.altPhone}` : ''}</div>
                              <div>العنوان: {order.customerInfo.address}, {order.customerInfo.city}</div>
                              {order.customerInfo.region && <div>المنطقة: {order.customerInfo.region}</div>}
                              {order.customerInfo.municipality && <div>البلدية: {order.customerInfo.municipality}</div>}
                              {order.customerInfo.postalCode && <div>الرمز البريدي: {order.customerInfo.postalCode}</div>}
                              {order.customerInfo.country && <div>الدولة: {order.customerInfo.country}</div>}
                            </div>
                          </div>
                          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4 md:col-span-2">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">عناصر الطلب</h3>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm">
                                <thead>
                                  <tr className="text-gray-500 dark:text-gray-300">
                                    <th className="text-right py-2">المنتج</th>
                                    <th className="text-right py-2">اللون</th>
                                    <th className="text-right py-2">السعر</th>
                                    <th className="text-right py-2">الكمية</th>
                                    <th className="text-right py-2">الإجمالي</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {order.items.map((it, idx) => (
                                    <tr key={idx} className="text-gray-800 dark:text-gray-200">
                                      <td className="py-2">{it.name}</td>
                                      <td className="py-2">
                                        {it.selectedColor ? (
                                          <div className="inline-flex items-center gap-2">
                                            <span
                                              className="inline-block h-4 w-4 rounded-full border border-gray-300 dark:border-dark-border"
                                              style={{ backgroundColor: it.selectedColor }}
                                              aria-label={`Selected color ${it.selectedColor}`}
                                            />
                                            <span>{it.selectedColor}</span>
                                          </div>
                                        ) : '-'}
                                      </td>
                                      <td className="py-2">{formatCurrency(it.price)}</td>
                                      <td className="py-2">{it.quantity}</td>
                                      <td className="py-2 font-medium">{formatCurrency(it.price * it.quantity)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-3 text-right text-gray-900 dark:text-white font-semibold">المجموع: {formatCurrency(order.totalAmount)}</div>
                          </div>
                          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4 md:col-span-3">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">معلومات إضافية</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700 dark:text-gray-300 items-end">
                              <div className="space-y-1">
                                <div className="text-xs text-gray-500 dark:text-gray-400">الحالة</div>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={order.status}
                                    onChange={(e) => updateOrderStatus(order._id, e.target.value as OrderStatus)}
                                    className="px-2 py-1 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                                  >
                                    {STATUS_OPTIONS.map(s => (
                                      <option key={s} value={s}>{STATUS_LABELS_AR[s]}</option>
                                    ))}
                                  </select>
                                  <span className={`px-2 py-1 rounded-full text-xs ${statusBadgeClass(order.status)}`}>{STATUS_LABELS_AR[order.status]}</span>
                                </div>
                              </div>
                              <div>رقم التتبع: {order.trackingNumber || '-'}</div>
                              <div>بوابة الدفع: {order.paymentMethod ? (PAYMENT_LABELS_AR[order.paymentMethod] || '-') : 'غير محدد'}</div>
                              <div>تم الإنشاء: {new Date(order.createdAt).toLocaleString('en-GB')}</div>
                              <div>آخر تحديث: {new Date(order.updatedAt).toLocaleString('en-GB')}</div>
                              <div>ملاحظات: {order.notes || '-'}</div>
                              <div className="md:col-span-3 flex justify-end">
                                <button
                                  onClick={() => requestDeleteOrder(order)}
                                  className="inline-flex items-center px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                                >
                                  <Trash2 className="w-4 h-4 ml-1" />
                                  حذف
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-between items-center">
        <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-2 text-sm bg-gray-100 dark:bg-dark-hover text-gray-700 dark:text-dark-text rounded disabled:opacity-50">السابق</button>
        <div className="text-sm text-gray-600 dark:text-gray-300">صفحة {page} من {totalPages}</div>
        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-2 text-sm bg-gray-100 dark:bg-dark-hover text-gray-700 dark:text-dark-text rounded disabled:opacity-50">التالي</button>
      </div>

      {orderPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border shadow-2xl p-6">
            <p className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
              هل انتي متأكدة من انك تريدي حذف هذا الطلب؟
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={cancelDeleteOrder}
                disabled={!!deletingOrderId}
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-dark-hover hover:bg-gray-200 dark:hover:bg-dark-bg text-gray-800 dark:text-dark-text transition-colors disabled:opacity-60"
              >
                لا
              </button>
              <button
                onClick={confirmDeleteOrder}
                disabled={!!deletingOrderId}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60"
              >
                {deletingOrderId ? 'جاري الحذف...' : 'نعم'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;


